// components/ModalSwitch.tsx

import React, { useEffect, useState } from 'react';

import './ModalLed.css';
import MQTTService from './MQTTService';
import jwt_decode from 'jwt-decode';

// 변경: slotIndex 등을 함께 저장
type ScheduleItem = {
  text: string;                 // "09:00 ~ 17:00 매일" 같은 표시용 문자열
  slotIndex: number | null;     // 보드에서 내려주는 si / slotIndex (없으면 null)
  startMinutes: number;
  endMinutes: number;
  repeatMode: 'daily' | 'weekly' | string;
  dayOfWeek: number[] | number;
};

// Timezones options (UTC offsets in seconds)
const timezones = [
  { label: 'UTC-12:00', value: -43200 },
  { label: 'UTC-11:00', value: -39600 },
  { label: 'UTC-10:00 (Hawaii)', value: -36000 },
  { label: 'UTC-09:00 (Alaska)', value: -32400 },
  { label: 'UTC-08:00 (Pacific Time)', value: -28800 },
  { label: 'UTC-07:00 (Mountain Time)', value: -25200 },
  { label: 'UTC-06:00 (Central Time)', value: -21600 },
  { label: 'UTC-05:00 (Eastern Time)', value: -18000 },
  { label: 'UTC-04:00 (Atlantic Time)', value: -14400 },
  { label: 'UTC-03:00', value: -10800 },
  { label: 'UTC-02:00', value: -7200 },
  { label: 'UTC-01:00', value: -3600 },
  { label: 'UTC+00:00 (London, GMT)', value: 0 },
  { label: 'UTC+01:00 (Paris, Berlin)', value: 3600 },
  { label: 'UTC+02:00 (Cairo)', value: 7200 },
  { label: 'UTC+03:00 (Moscow, Istanbul)', value: 10800 },
  { label: 'UTC+04:00 (Dubai)', value: 14400 },
  { label: 'UTC+05:00 (Karachi)', value: 18000 },
  { label: 'UTC+05:30 (India)', value: 19800 },
  { label: 'UTC+06:00 (Dhaka)', value: 21600 },
  { label: 'UTC+07:00 (Bangkok, Jakarta)', value: 25200 },
  { label: 'UTC+08:00 (Beijing, Singapore)', value: 28800 },
  { label: 'UTC+09:00 (Seoul, Tokyo)', value: 32400 },
  { label: 'UTC+09:30', value: 34200 },
  { label: 'UTC+10:00 (Sydney)', value: 36000 },
  { label: 'UTC+11:00', value: 39600 },
  { label: 'UTC+12:00 (Auckland)', value: 43200 },
];


interface ModalSwitchProps {
  deviceMac: string;
  onCancel: () => void;
  labelNo: number;
  currentLabel: string;
  mqttMessage: string;
}

const ModalSwitch: React.FC<ModalSwitchProps> = ({
  deviceMac,
  onCancel,
  labelNo,
  currentLabel,
  mqttMessage,
}) => {
  const [email, setEmail] = useState<string | null>(null);
  const [startTime, setStartTime] = useState('09:00:00');
  const [endTime, setEndTime] = useState('17:00:00');
  const [repeatMode, setRepeatMode] = useState<'daily' | 'weekly' | string>('daily');
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [scheduleLogs, setScheduleLogs] = useState<ScheduleItem[]>([]);
  const [boardTime, setBoardTime] = useState<string | null>(null);
  const [tzOffset, setTzOffset] = useState<number>(32400); // Default to UTC+09:00 (Seoul)
  const pendingDeleteRef = React.useRef<Set<string>>(new Set());

  // slotIndex 포함 키 (기존)
  const makeKey = (it: ScheduleItem) =>
    it.slotIndex != null
      ? `si:${it.slotIndex}`
      : `s:${it.startMinutes}|e:${it.endMinutes}|r:${it.repeatMode}|dow:${JSON.stringify(it.dayOfWeek)}`;

  // slotIndex는 무시하고 "내용"만으로 만드는 키
  const makeContentKey = (it: ScheduleItem) =>
    `s:${it.startMinutes}|e:${it.endMinutes}|r:${it.repeatMode}|dow:${JSON.stringify(it.dayOfWeek)}`;

  // 중복 방지용 ref
  const seenSetRef = React.useRef<Set<string>>(new Set());
  // 첫 번째 list 응답 감지용 ref
  const isFirstListRef = React.useRef(true);

  // MQTT 안전 발행 함수
  const publishSafe = (msg: any) => {
    if (!email) {
      console.warn('email not ready, skip publish:', msg);
      return;
    }
    const topic = `i2r/${email}/in`;
    MQTTService.getInstance().publish(topic, JSON.stringify(msg));
  };

  // MQTT list 응답 처리
  useEffect(() => {
    if (!mqttMessage) return;

    try {
      const msg = JSON.parse(mqttMessage);

      const mac = msg.mac ?? msg.m;
      if (mac !== deviceMac) return;

      // gs 응답 처리 (이 창에서 보낸 시간 요청 응답)
      if (msg.command === 'getStatus' || msg.c === 'gs') {
        if (msg.time) {
          setBoardTime(msg.time);
        }
        if (msg.tz !== undefined) {
          setTzOffset(msg.tz);
        }
      }

      // list 응답 + 내 장치/내 핀만 처리
      const isList = msg.operation === 'list' || msg.o === 'l';
      const pin = msg.pinIndex ?? msg.pi ?? msg.i;

      // 로그확인용 (개발시 유용, 필요없으면 주석)
      // console.log('MQTT Check:', { isList, mac, pin, deviceMac, labelNo });

      if (!isList || pin !== labelNo) return;

      // 첫 응답에서만 화면 초기화
      if (isFirstListRef.current) {
        isFirstListRef.current = false;
        setScheduleLogs([]);
        seenSetRef.current.clear();
      }

      // full/축약/배열 응답 모두 수용
      const candidates = Array.isArray(msg.items || msg.list || msg.sl)
        ? (msg.items || msg.list || msg.sl)
        : [msg];

      const dayMap = ['일', '월', '화', '수', '목', '금', '토'];
      const normalizeDow = (v: any) => (Array.isArray(v) ? v : [v]);
      const z = (n: number) => String(n).padStart(2, '0');
      const newItems: ScheduleItem[] = [];

      // ✅ [복구] decompressMessage 대신 직접 파싱 방식으로 복원
      for (const it of candidates) {
        // 보드가 주는 키: start, end, rm, dw, index, pn 등
        const sMin = it.startMinutes ?? it.start ?? it.s;
        const eMin = it.endMinutes ?? it.end ?? it.e;

        if (typeof sMin !== 'number' || typeof eMin !== 'number') continue;

        const sH = Math.floor(sMin / 3600);
        const sM = Math.floor((sMin % 3600) / 60);
        const sS = sMin % 60;
        const eH = Math.floor(eMin / 3600);
        const eM = Math.floor((eMin % 3600) / 60);
        const eS = eMin % 60;

        const startStr = `${z(sH)}:${z(sM)}:${z(sS)}`;
        const endStr = `${z(eH)}:${z(eM)}:${z(eS)}`;

        const repeatModeRaw = it.repeatMode ?? it.rm ?? it.r ?? 'daily';
        const repeatModeVal = String(repeatModeRaw).toLowerCase() as 'daily' | 'weekly' | string;
        const dow = it.dayOfWeek ?? it.dw ?? it.dow ?? 0;

        const repeatStr =
          repeatModeVal === 'weekly'
            ? normalizeDow(dow).map((d: number) => dayMap[d]).join(', ')
            : '매일';

        // ✅ 핵심 수정: index, sI, si 모두 체크 (보드가 index로 보내고 있었음)
        const si = it.slotIndex ?? it.si ?? it.sI ?? it.index;

        const newItem: ScheduleItem = {
          text: `${startStr} ~ ${endStr} ${repeatStr}`,
          slotIndex: si ?? null,
          startMinutes: sMin,
          endMinutes: eMin,
          repeatMode: repeatModeVal,
          dayOfWeek: dow,
        };

        // ── ① 사용자가 방금 삭제한 "내용키"면 재삽입 차단 ──
        const contentKey = makeContentKey(newItem);
        if (pendingDeleteRef.current.has(contentKey)) {
          // 만약 보드가 진짜로 지워서 si 달고 왔다면 성공 처리하고 해제
          if (newItem.slotIndex != null) {
            pendingDeleteRef.current.delete(contentKey);
          }
          continue;
        }

        // ── ② 기존 무-si 항목과 매칭되면 si 업그레이드(병합) ──
        let merged = false;
        setScheduleLogs(prev => {
          const next = prev.map(old => {
            if (
              old.slotIndex == null &&
              makeContentKey(old) === contentKey &&
              newItem.slotIndex != null
            ) {
              merged = true;
              return { ...old, slotIndex: newItem.slotIndex };
            }
            return old;
          });
          return next;
        });
        if (merged) continue;

        // ── ③ 중복검사 후 추가 ──
        const key = makeKey(newItem);
        if (seenSetRef.current.has(key)) continue;
        seenSetRef.current.add(key);
        newItems.push(newItem);
      }

      if (newItems.length) {
        setScheduleLogs(prev => {
          const merged = [...prev, ...newItems];
          return merged.sort(
            (a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes
          );
        });
      }
    } catch (error) {
      console.error('Error parsing MQTT message:', error);
    }
  }, [mqttMessage, deviceMac, labelNo]);


  // MAC/핀 변경 시 초기화
  useEffect(() => {
    setScheduleLogs([]);
    seenSetRef.current.clear();
    isFirstListRef.current = true;
    if (email) handleView();
  }, [deviceMac, labelNo, email]);

  // 이메일 로드
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken: any = jwt_decode(token);
        setEmail(decodedToken.email);
      } catch (error) {
        console.error('토큰 디코딩 오류:', error);
      }
    } else {
      console.error('토큰이 없습니다.');
    }
  }, []);

  // 보기 요청
  const handleView = () => {
    isFirstListRef.current = true;
    publishSafe({
      command: 'schedule',
      operation: 'list',
      mac: deviceMac,
      pinIndex: labelNo,
    });
    // 현재 시간 및 Timezone 정보(gs) 요청
    setTimeout(() => {
      publishSafe({
        command: 'getStatus',
        mac: deviceMac,
      });
    }, 100);
  };

  // 시간 및 Timezone 설정 동기화
  const handleTimeSync = () => {
    // 보드의 시간 체계가 UTC+Offset을 기준으로 직접 표시되므로, 
    // 현재 브라우저의 UTC 시간(초)에 선택한 타임존(tzOffset)을 더한 '지역 시간'을 만들어 보냅니다.
    const localEpoch = Math.floor(Date.now() / 1000) + tzOffset;
    
    // MQTT 전송
    publishSafe({
      command: 'setTime',
      mac: deviceMac,
      epoch: localEpoch,
      tz_offset: tzOffset,
    });
    
    // 약간 지연 후 변경된 시간 다시 요청하여 화면 갱신 유도
    setTimeout(() => {
      publishSafe({
        command: 'getStatus',
        mac: deviceMac,
      });
    }, 1000);
  };

  // 저장 요청
  const handleSave = () => {
    const sParts = startTime.split(':').map(Number);
    const sH = sParts[0] || 0;
    const sM = sParts[1] || 0;
    const sS = sParts[2] || 0;

    const eParts = endTime.split(':').map(Number);
    const eH = eParts[0] || 0;
    const eM = eParts[1] || 0;
    const eS = eParts[2] || 0;

    const start = sH * 3600 + sM * 60 + sS;
    const end = eH * 3600 + eM * 60 + eS;

    // ✅ 새 항목을 UI에 즉시 추가 (즉각 반영)
    const dayMap = ['일', '월', '화', '수', '목', '금', '토'];
    const z = (n: number) => String(n).padStart(2, '0');
    const normalizeDow = (v: any) => (Array.isArray(v) ? v : [v]);

    const startStr = `${z(sH)}:${z(sM)}:${z(sS)}`;
    const endStr = `${z(eH)}:${z(eM)}:${z(eS)}`;
    const repeatStr =
      repeatMode === 'weekly'
        ? normalizeDow(selectedDays)
          .map((d: number) => dayMap[d])
          .join(', ')
        : '매일';

    const newItem: ScheduleItem = {
      text: `${startStr} ~ ${endStr} ${repeatStr}`,
      slotIndex: null, // 실제 slotIndex는 보드 list 응답 후 업데이트됨
      startMinutes: start,
      endMinutes: end,
      repeatMode: repeatMode,
      dayOfWeek: repeatMode === 'weekly' ? selectedDays : 0,
    };

    // ✅ 중복 방지 로직에 따라 병합
    setScheduleLogs((prev) => {
      const merged = [...prev, newItem];
      return merged.sort(
        (a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes
      );
    });

    // ✅ 낙관 반영 항목도 중복키에 등록 (list 응답에서 중복 방지)
    seenSetRef.current.add(makeKey(newItem));

    // ✅ MQTT로 insert 메시지 발행
    const sendMessage: any = {
      command: 'schedule',
      operation: 'insert',
      mac: deviceMac,
      pinIndex: labelNo,
      startMinutes: start,
      endMinutes: end,
      repeatMode: repeatMode,
      dayOfWeek: repeatMode === 'weekly' ? selectedDays : 0,
    };
    publishSafe(sendMessage);

    // ✅ 서버의 실제 list 반영을 기다려 재동기화
    setTimeout(() => {
      handleView();
    }, 1200);
  };


  // 전체 삭제
  const handleDeleteAll = () => {
    publishSafe({
      command: 'schedule',
      mac: deviceMac,
      operation: 'deleteAll',
      pinIndex: labelNo,
    });
    setScheduleLogs([]);
    setTimeout(handleView, 500);
  };

  // 개별 삭제 (항목 객체 기반)
  const handleDelete = (item: ScheduleItem) => {
    // 1) 서버 삭제
    if (item.slotIndex == null) {
      // ⚠️ 보드가 si를 아직 안 준 경우(방금 추가된 항목 등) — 내용기반 삭제 시도
      publishSafe({
        command: 'schedule',
        mac: deviceMac,
        operation: 'delete',
        pinIndex: labelNo,
        // 서버가 내용기반 삭제를 지원하지 않으면, list 동기화만 유도
        // (가능하면 펌웨어가 slotIndex를 항상 내려주도록 맞추는 게 최선)
        startMinutes: item.startMinutes,
        endMinutes: item.endMinutes,
        repeatMode: item.repeatMode,
        dayOfWeek: item.dayOfWeek,
      });
      // 보드가 내용기반 삭제를 지원하지 않아도, 다음 list에서 재삽입을 차단
      pendingDeleteRef.current.add(makeContentKey(item));
    } else {
      // ✅ 권장: slotIndex로 정확 삭제
      publishSafe({
        command: 'schedule',
        mac: deviceMac,
        operation: 'delete',
        pinIndex: labelNo,
        slotIndex: item.slotIndex,
      });
    }

    // 2) UI 즉시 반영(고유키로 제거)
    const k = makeKey(item);
    setScheduleLogs(prev => prev.filter(p => makeKey(p) !== k));

    // 3) 서버 반영 확인 후 재동기화
    setTimeout(handleView, 600);
  };

  const toggleDaySelection = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>{currentLabel}</h3>

        {/* 모던한 기기 시간 및 동기화 패널 */}
        <div style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          marginTop: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#475569' }}>
              <span style={{ fontSize: '1.2em' }}>🕒</span>
              <span style={{ fontSize: '0.95em', fontWeight: '500' }}>기기 현재 시간</span>
            </div>
            {boardTime ? (
              <span style={{ 
                fontSize: '1.1em', 
                fontWeight: '600', 
                color: '#0f172a',
                fontFamily: 'monospace',
                backgroundColor: '#fff',
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                minWidth: '85px',
                textAlign: 'center'
              }}>
                {boardTime}
              </span>
            ) : (
              <span style={{ fontSize: '0.9em', color: '#94a3b8' }}>로딩 중...</span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <select 
              value={tzOffset} 
              onChange={(e) => setTzOffset(Number(e.target.value))}
              style={{ 
                fontSize: '0.9em', 
                padding: '6px 10px', 
                borderRadius: '6px', 
                border: '1px solid #cbd5e1', 
                backgroundColor: '#ffffff',
                color: '#334155',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                maxWidth: '220px'
              }}
              title="세계 표준시 (Timezone) 선택"
            >
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
            <button 
              onClick={handleTimeSync} 
              style={{ 
                fontSize: '0.9em', 
                padding: '6px 14px', 
                cursor: 'pointer', 
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
              title="선택한 표준시와 현재 브라우저 시간 바탕으로 기기 시간 동기화"
            >
              <span>♻️</span> 동기화
            </button>
          </div>
        </div>

        {/* 스케줄 목록 (Table Style) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px', gap: '8px' }}>
          <h4 style={{ margin: 0, whiteSpace: 'nowrap', color: '#1e293b', borderLeft: '4px solid #3b82f6', paddingLeft: '8px' }}>설정된 스케줄 목록</h4>
        </div>
        <div className="modal-table-container">
          <table className="modal-table">
            <thead>
              <tr>
                <th>번호</th>
                <th>시간 / 반복</th>
                <th>삭제</th>
              </tr>
            </thead>
            <tbody>
              {scheduleLogs.length > 0 ? (
                scheduleLogs.map((item, index) => (
                  <tr key={item.slotIndex != null ? `si-${item.slotIndex}` : `idx-${index}`}>
                    <td>
                      {item.slotIndex != null ? `${item.slotIndex}` : `[${index}]`}
                    </td>
                    <td>{item.text}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        className="btn-delete-icon"
                        onClick={() => handleDelete(item)}
                        title="삭제"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="empty-message">
                    등록된 스케줄이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 입력 필드 */}
        <h4>스케줄 추가</h4>
        <div className="form-group">
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>시작 시간</label>
            <input
              type="time"
              step="1"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>종료 시간</label>
            <input
              type="time"
              step="1"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>반복 설정</label>
            <select
              value={repeatMode}
              onChange={(e) => setRepeatMode(e.target.value)}
            >
              <option value="daily">매일</option>
              <option value="weekly">매주</option>
            </select>
          </div>

          {repeatMode === 'weekly' && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>요일 선택</label>
              <div className="day-selector-container">
                {['일', '월', '화', '수', '목', '금', '토'].map((day, index) => (
                  <button
                    key={index}
                    className={`day-selector-btn ${selectedDays.includes(index) ? 'selected' : ''}`}
                    onClick={() => toggleDaySelection(index)}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 버튼 */}
        <div className="modal-actions">
          <button className="modal-btn btn-view" onClick={handleView}>보기</button>
          <button className="modal-btn btn-save" onClick={handleSave}>저장</button>
          <button className="modal-btn btn-delete-all" onClick={handleDeleteAll}>모두 지우기</button>
          <button className="modal-btn btn-cancel" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>
  );
};

export default ModalSwitch;
